import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import * as Sentry from '@sentry/browser';
import { compose } from 'recompose';
import format from 'string-template';

import Coach from '../../../../../../../Model/Coach';
import Product from '../../../../../../../Model/Product';
import LoggedInUserContext from '../../../../../../../context/LoggedInUserContext';
import useComponentMounted from '../../../../../../../hooks/useComponentMounted';
import useLogger from '../../../../../../../hooks/useLogger';
import useToast from '../../../../../../hooks/useToast';
import MultiSelectFilter from '../../../../../../components/MultiSelectFilter';
import AssignmentNotificationSetting from '../AssignmentNotificationSetting';
import {
  SettingsActionContainer,
  ActionLabel,
  SubTitle,
} from '../WorkoutNotification/styles';
import texts from './texts.json';

const SETTING_CHANGED_EVENT = 'newClientNotificationSettingChanged';

/**
 * The product's label in the picker: its name plus the one tag that matters most for a long list,
 * so a coach can tell an archived or free product from a live paid one.
 * @param {Product} product
 * @return {string}
 */
const productLabel = (product) => {
  let tag;
  if (product.isChallenge) tag = texts.tags.challenge;
  else if (product.isHyroxProduct) tag = texts.tags.hyrox;
  else if (product.isDefault) tag = texts.tags.free;
  return tag ? `${product.name} (${tag})` : product.name;
};

const sameIds = (first, second) => first.length === second.length && first.every((id) => second.includes(id));

/*
  The coach's new-client push (S2-9567): a push to their phone when a client subscribes to one of their
  products, sent from the same trigger as the Slack message. Off by default with no product tagged. The
  coach turns it on and picks the products that may send it; a push fires only when both hold. Saved
  with who changed it, since an admin viewing as the coach can change it too.
*/
const NewClientPushNotification = ({ coachDoc }) => {
  const { userId, userDoc } = useContext(LoggedInUserContext);
  const { showToast } = useToast();
  const { logEvent } = useLogger();
  const isComponentMountedRef = useComponentMounted();

  const [products, setProducts] = useState(null);
  const [hasProductsError, setHasProductsError] = useState(false);

  const { enabled, productIds, allProducts } = coachDoc.newClientPushConfig;
  const updatedBy = useMemo(() => ({ uid: userId, name: userDoc?.name ?? '' }), [userId, userDoc?.name]);

  // Every product the coach can still sell, hidden and the free default included, A–Z. Archived
  // products are left out: nobody can subscribe to one, so a notification for it never fires. A
  // selection that covers the whole list is saved as `allProducts`, which the server honours over the
  // list, so a product created later notifies too. The picker cannot tell its All option from every
  // box ticked by hand, and both read as "all of them".
  useEffect(() => {
    let isActive = true;
    Product.getProductByCoachId(coachDoc.id)
      .then((collection) => {
        if (!isActive) return;
        setProducts(
          collection.docs
            .filter((product) => !product.isArchived)
            .sort((first, second) => first.name.localeCompare(second.name)),
        );
      })
      .catch((error) => {
        Sentry.captureException(error, { extra: { coachId: coachDoc.id } });
        if (isActive) setHasProductsError(true);
      });
    return () => {
      isActive = false;
    };
  }, [coachDoc.id]);

  const productOptions = useMemo(
    () => (products ?? []).map((product) => ({ value: product.id, label: productLabel(product) })),
    [products],
  );
  const optionIds = useMemo(() => productOptions.map((option) => option.value), [productOptions]);

  // What the picker shows on mount: every product under `allProducts`, else the saved ids it can show. A
  // saved id the picker lacks (archived since) is not a change the coach made, so it is left out of the
  // comparison too; it is dropped only once the coach changes the selection.
  const shownSelection = useMemo(
    () => (allProducts ? optionIds : productIds.filter((id) => optionIds.includes(id))),
    [allProducts, optionIds, productIds],
  );

  const save = useCallback(async (config) => {
    await coachDoc.setNewClientPushConfig(config, updatedBy);
    logEvent(SETTING_CHANGED_EVENT, {
      coachId: coachDoc.id,
      enabled: config.enabled,
      productCount: config.productIds.length,
      allProducts: config.allProducts,
    });
  }, [coachDoc, updatedBy, logEvent]);

  const handleToggle = useCallback(
    (value) => save({ enabled: value, productIds, allProducts }),
    [save, productIds, allProducts],
  );

  const handleProductsSelected = useCallback(async (selectedIds) => {
    // The picker reports its initial selection on mount; only a change is a save.
    if (sameIds(selectedIds, shownSelection)) return;
    const coversAll = optionIds.length > 0 && selectedIds.length === optionIds.length;
    try {
      await save({ enabled, productIds: selectedIds, allProducts: coversAll });
      if (isComponentMountedRef.current) showToast(texts.productsSaved);
    } catch (error) {
      Sentry.captureException(error, { extra: { coachId: coachDoc.id } });
      if (isComponentMountedRef.current) {
        showToast(format(texts.productsError, { error: error.message }), { error: true });
      }
    }
  }, [enabled, shownSelection, optionIds, save, showToast, isComponentMountedRef, coachDoc.id]);

  const renderProductPicker = () => {
    if (hasProductsError) {
      return <SubTitle>{texts.productsLoadError}</SubTitle>;
    }
    if (!products) {
      return null;
    }
    return (
      <>
        <SettingsActionContainer>
          <ActionLabel>{texts.productsLabel}</ActionLabel>
          <MultiSelectFilter
            description={texts.productsPlaceholder}
            options={productOptions}
            initialValues={shownSelection}
            onValuesSelected={handleProductsSelected}
          />
        </SettingsActionContainer>
        {allProducts && <SubTitle>{texts.allProductsSelected}</SubTitle>}
        {!allProducts && productIds.length === 0 && <SubTitle>{texts.noProductsSelected}</SubTitle>}
      </>
    );
  };

  return (
    <>
      <AssignmentNotificationSetting
        isEnabled={enabled}
        onSave={handleToggle}
        texts={texts}
      />
      {enabled && renderProductPicker()}
    </>
  );
};

NewClientPushNotification.propTypes = {
  coachDoc: PropTypes.instanceOf(Coach).isRequired,
};

export default compose(
  observer,
)(NewClientPushNotification);
